// Google Ads click identifier (gclid) capture + 90-day persistence.
//
// We accept the click identifier from any of these URL params:
//   ?gclid=   raw click id, set when Google appends it to the ad-landing URL
//   ?wbraid=  iOS App Tracking Transparency variant (web → app)
//   ?gbraid=  iOS App Tracking Transparency variant (web → web)
//   ?_gcl_aw= encoded cookie value forwarded by the WordPress
//             marketing site (fotodelavnica.si etc.) when a user
//             clicks through to the order subdomain. By the time
//             they reach checkout, the raw ?gclid= is long gone from
//             the URL bar; only the _gcl_aw cookie still holds it.
//             The WP script appends that cookie as ?_gcl_aw=...&
//             so we must parse it here.
//
// Google Ads attribution window is 90 days; we expire after the same.

const STORAGE_KEY = "gclid_v1";
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

type StoredClickId = {
  value: string;
  capturedAt: number; // ms epoch
};

function readUrlParam(name: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return new URL(window.location.href).searchParams.get(name);
  } catch {
    return null;
  }
}

// _gcl_aw cookie format is "<version>.<status>.<gclid>" e.g.
// "1.1.AbCdEfGhIjK..." or "GCL.1234567890.AbCdEf...". The actual
// gclid is the last dot-separated segment.
function extractGclidFromGclAw(raw: string | null): string | null {
  if (!raw) return null;
  const parts = raw.split(".");
  const last = parts[parts.length - 1];
  return last && last.length > 10 ? last : null;
}

export function captureGclidFromUrl(): void {
  if (typeof window === "undefined") return;
  const value =
    readUrlParam("gclid") ||
    readUrlParam("wbraid") ||
    readUrlParam("gbraid") ||
    extractGclidFromGclAw(readUrlParam("_gcl_aw"));
  if (!value) return;
  try {
    const payload: StoredClickId = { value, capturedAt: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be disabled (private mode, quota); silently skip.
  }
}

export function getStoredGclid(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredClickId;
    if (!parsed?.value || typeof parsed.capturedAt !== "number") return null;
    if (Date.now() - parsed.capturedAt > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
}
